import React, { useEffect, useState } from 'react';
import './termsofuse.css';

const TermsOfService = () => {
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -70% 0px',
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, observerOptions);

    const sections = document.querySelectorAll('section[id]');
    sections.forEach((section) => observer.observe(section));

    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  const scrollToSection = (e, id) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveId(id);
    }
  };

  const navItems = [
    { id: 'section-1', label: '1. Definitions' },
    { id: 'section-2', label: '2. Service Description' },
    { id: 'section-3', label: '3. Service Access' },
    { id: 'section-4', label: '4. Recording Compliance' },
    { id: 'section-5', label: '5. Email & Communication Access' },
    { id: 'section-6', label: '6. Autonomous Actions & Approvals' },
    { id: 'section-7', label: '7. Knowledge Graph & Memory' },
    { id: 'section-8', label: '8. AI & Data Use' },
    { id: 'section-9', label: '9. Fees & Renewal' },
    { id: 'section-10', label: '10. Proprietary Rights' },
    { id: 'section-11', label: '11. Confidentiality' },
    { id: 'section-12', label: '12. Warranties' },
    { id: 'section-13', label: '13. Limitation of Liability' },
    { id: 'section-14', label: '14. Term & Termination' },
    { id: 'section-15', label: '15. Dispute Resolution' },
    { id: 'section-16', label: '16. Future Features' },
  ];

  return (
    <div className="terms-container">
      <main className="main-content">
        <h1>HiCapy Terms of Service</h1>
        <p><strong>Last Updated:</strong> December 31, 2025</p>
        <p>
          This Terms of Service ("Agreement") is entered into by and between HiCapy ("HiCapy," "we," or "us") and the entity or person placing an order for or accessing any Services ("Customer" or "you").
        </p>
        <p className="caps-warning">
          IMPORTANT NOTICE: THIS AGREEMENT CONTAINS A BINDING ARBITRATION PROVISION AND WAIVER OF JURY TRIALS AND CLASS ACTIONS GOVERNING DISPUTES ARISING FROM USE OF THE SERVICES. IT ALSO CONTAINS PROVISIONS REGARDING AUTOMATIC RENEWAL OF SUBSCRIPTIONS, AI-POWERED AUTONOMOUS ACTIONS, AND ACCESS TO YOUR EMAIL AND CALENDAR DATA.
        </p>

        <section id="section-1">
          <h2>1. Definitions</h2>
          <ul>
            <li><strong>"AI Assistant"</strong> means HiCapy's artificial intelligence system that acts as your digital chief of staff, managing meetings, emails, scheduling, and other tasks on your behalf.</li>
            <li><strong>"AI Features"</strong> means features utilizing large language models, speech-to-text, natural language processing, and machine learning to generate summaries, transcripts, action items, email drafts, and automated responses.</li>
            <li><strong>"Autonomous Actions"</strong> means actions taken by the AI Assistant on your behalf, including but not limited to: sending emails, scheduling meetings, forwarding messages, creating calendar events, and responding to requests, subject to your approval settings.</li>
            <li><strong>"Approval System"</strong> means the chat-based interface through which HiCapy requests your permission before executing Autonomous Actions.</li>
            <li><strong>"Connected Services"</strong> means third-party services you authorize HiCapy to access, including but not limited to Gmail, Google Calendar, Slack, Notion, and other productivity tools.</li>
            <li><strong>"Customer Data"</strong> means all electronic data you submit to the Service, including audio recordings, transcripts, emails, calendar events, messages from Connected Services, documents, and metadata.</li>
            <li><strong>"Knowledge Graph"</strong> means HiCapy's personalized database that stores and organizes your Customer Data, including entities, relationships, conversation threads, topics, and contextual information for intelligent retrieval and response generation.</li>
            <li><strong>"Output"</strong> means the text, summaries, drafts, responses, and content generated by the AI Features based on Customer Data.</li>
            <li><strong>"Recording Laws"</strong> means all applicable local, state, national, and international laws governing the monitoring, interception, or recording of conversations.</li>
          </ul>
        </section>

        <section id="section-2">
          <h2>2. Service Description</h2>
          <p>HiCapy provides an AI-powered digital assistant platform that:</p>
          <ul>
            <li>Joins and records video meetings with participant consent</li>
            <li>Generates meeting transcripts, summaries, and action items</li>
            <li>Monitors and processes your emails from Connected Services</li>
            <li>Manages your calendar and schedules meetings autonomously</li>
            <li>Drafts and sends emails on your behalf with your approval</li>
            <li>Maintains a personal Knowledge Graph of your work context</li>
            <li>Retrieves relevant information from past conversations, meetings, and documents</li>
            <li>Performs other administrative tasks as specified in your service plan</li>
          </ul>
          <p>The specific features available depend on your subscription tier (Free, Pro, or Enterprise).</p>
        </section>

        <section id="section-3">
          <h2>3. Service Access and Restrictions</h2>
          <h3>3.1 Access Grant</h3>
          <p>Subject to your compliance with this Agreement, HiCapy grants you a limited, non-exclusive, non-transferable right to access and use the Services for your internal business purposes.</p>
          <h3>3.2 Restrictions</h3>
          <p>You shall not:</p>
          <ul>
            <li>Use the Service to record meetings without the consent of all participants where required by law.</li>
            <li>Access emails, messages, or documents without proper authorization from their owners.</li>
            <li>Use the Service to send spam, phishing attempts, or unsolicited commercial messages.</li>
            <li>Reverse engineer, copy, or create derivative works of the HiCapy platform or models.</li>
            <li>Use the Services to build a competitive product or service.</li>
            <li>Send infringing, obscene, harassing, or unlawful material via the Service.</li>
            <li>Attempt to circumvent the Approval System or override safety mechanisms.</li>
            <li>Share your account credentials with unauthorized parties.</li>
          </ul>
        </section>

        <section id="section-4">
          <h2>4. Meeting Recording and Compliance</h2>
          <h3>4.1 Responsibility for Consent</h3>
          <p>You acknowledge that HiCapy acts as your agent in recording meetings. You represent and warrant that you will obtain all necessary consents from meeting participants prior to enabling HiCapy to record or transcribe any conversation.</p>
          <h3>4.2 Recording Notifications</h3>
          <p>HiCapy's bot will identify itself when joining meetings, but you remain responsible for ensuring compliance with all applicable Recording Laws in your jurisdiction and the jurisdictions of all meeting participants.</p>
          <h3>4.3 Indemnification for Recording Violations</h3>
          <p>You agree to indemnify and hold HiCapy harmless from any claims, damages, or fines arising out of your failure to comply with Recording Laws or your failure to notify participants that they are being recorded.</p>
        </section>

        <section id="section-5">
          <h2>5. Email and Communication Access</h2>
          <h3>5.1 Authorization to Access</h3>
          <p>By connecting your email accounts and other communication services to HiCapy, you authorize HiCapy to:</p>
          <ul>
            <li>Read incoming and outgoing emails from your connected accounts</li>
            <li>Access email metadata (sender, recipient, subject, timestamps)</li>
            <li>Access message content from Slack, Notion, and other Connected Services</li>
            <li>Monitor email threads and conversation history</li>
            <li>Access attachments and documents referenced in communications</li>
          </ul>
          <h3>5.2 Processing and Filtering</h3>
          <p>HiCapy will process your communications to distinguish between work-related content and spam/promotional material. You acknowledge that HiCapy uses AI to classify emails and may occasionally misclassify messages.</p>
          <h3>5.3 Revocation of Access</h3>
          <p>You may revoke HiCapy's access to any Connected Service at any time through your account settings or through the respective service provider's authorization management. Revocation may limit or disable certain HiCapy features.</p>
          <h3>5.4 Privacy and Confidentiality</h3>
          <p>HiCapy will treat all accessed communications as Confidential Information subject to Section 11 of this Agreement. We will not share your communications with third parties except as necessary to provide the Services or as required by law.</p>
        </section>

        <section id="section-6">
          <h2>6. Autonomous Actions and Approval System</h2>
          <h3>6.1 Autonomous Action Framework</h3>
          <p>HiCapy is designed to take actions on your behalf, including:</p>
          <ul>
            <li>Drafting and sending email responses</li>
            <li>Creating, modifying, or canceling calendar events</li>
            <li>Scheduling meetings with internal and external participants</li>
            <li>Forwarding emails or messages to specified recipients</li>
            <li>Adding tasks or notes to Connected Services</li>
            <li>Sharing documents or files when responding to requests</li>
          </ul>
          <h3>6.2 Approval Requirements</h3>
          <p>Before executing most Autonomous Actions, HiCapy will request your approval through the chat-based Approval System. You may:</p>
          <ul>
            <li><strong>Approve:</strong> Allow HiCapy to execute the proposed action immediately</li>
            <li><strong>Edit:</strong> Modify the proposed action before execution</li>
            <li><strong>Reject:</strong> Cancel the proposed action entirely</li>
          </ul>
          <h3>6.3 Auto-Approval Settings</h3>
          <p>You may configure certain actions to execute automatically without explicit approval (e.g., "always accept internal meeting requests"). By enabling auto-approval for specific action types, you acknowledge and accept responsibility for those automated actions.</p>
          <h3>6.4 High-Stakes Actions</h3>
          <p className="caps-warning">CERTAIN HIGH-STAKES ACTIONS WILL ALWAYS REQUIRE EXPLICIT APPROVAL, INCLUDING: sending emails to large distribution lists, actions involving financial commitments, legal documents, or confidential information marked as sensitive, and actions that delete or permanently modify data.</p>
          <h3>6.5 Your Responsibility</h3>
          <p>You are fully responsible for all actions taken through your HiCapy account, whether approved manually or automatically. You should review proposed actions carefully before approving them.</p>
          <h3>6.6 Response Times</h3>
          <p>Approval requests will remain pending for 24 hours unless you specify otherwise. After 24 hours without response, proposed actions will be marked as expired and not executed.</p>
        </section>

        <section id="section-7">
          <h2>7. Knowledge Graph and Memory System</h2>
          <h3>7.1 Knowledge Graph Creation</h3>
          <p>HiCapy creates and maintains a personalized Knowledge Graph that stores:</p>
          <ul>
            <li>Entities (people, companies, projects, topics)</li>
            <li>Relationships between entities</li>
            <li>Conversation threads and email chains</li>
            <li>Meeting summaries and action items</li>
            <li>Temporal context (when events occurred)</li>
            <li>Document references and locations</li>
            <li>Your preferences and communication patterns</li>
          </ul>
          <h3>7.2 Data Retrieval and Context</h3>
          <p>HiCapy uses the Knowledge Graph to provide contextually relevant responses by retrieving information from your past communications, meetings, and documents. This enables HiCapy to answer questions like "Who requested the Q4 report?" or "When did we last discuss the marketing campaign?"</p>
          <h3>7.3 Memory Persistence</h3>
          <p>Your Knowledge Graph persists across sessions and is continuously updated as you use the Service. You may request deletion of specific entries or your entire Knowledge Graph at any time.</p>
          <h3>7.4 Learning and Adaptation</h3>
          <p>HiCapy learns from your approval decisions, corrections, and preferences to improve future suggestions. For example, if you consistently edit email drafts to use a more formal tone, HiCapy will adapt its future drafts accordingly.</p>
          <h3>7.5 Data Isolation</h3>
          <p>Your Knowledge Graph is isolated from other users' data. HiCapy does not share information between different customer accounts.</p>
        </section>

        <section id="section-8">
          <h2>8. Artificial Intelligence and Data Use</h2>
          <h3>8.1 AI Output Disclaimer</h3>
          <p className="caps-warning">HICAPY USES PROBABILISTIC AI TECHNOLOGY. YOU ACKNOWLEDGE THAT OUTPUT (TRANSCRIPTS, SUMMARIES, EMAIL DRAFTS, RESPONSES) MAY CONTAIN INACCURACIES, ERRORS, OR "HALLUCINATIONS." OUTPUT IS PROVIDED "AS IS" AND HICAPY MAKES NO WARRANTIES REGARDING ITS ACCURACY, COMPLETENESS, OR SUITABILITY FOR LEGAL, MEDICAL, FINANCIAL, OR OTHER CRITICAL DECISION-MAKING.</p>
          <p>You should review and verify all AI-generated Output before relying on it or allowing it to be sent to others.</p>
          <h3>8.2 Third-Party AI Services</h3>
          <p>HiCapy uses third-party AI providers (including Anthropic Claude, OpenAI, and others) to power certain features. Your Customer Data may be processed by these providers in accordance with their respective terms of service and privacy policies.</p>
          <h3>8.3 Data Training Rights</h3>
          <ul>
            <li><strong>For Enterprise Plans:</strong> HiCapy will not use your Customer Data to train its generalized artificial intelligence models. Your data remains private and is used solely to provide services to you.</li>
            <li><strong>For Pro Plans:</strong> HiCapy will not use identifiable Customer Data to train generalized models, but may use anonymized, aggregated data for model improvement.</li>
            <li><strong>For Free Plans:</strong> You grant HiCapy a non-exclusive, royalty-free license to use anonymized Customer Data to improve our algorithms and train our AI models.</li>
          </ul>
          <h3>8.4 Spam and Content Filtering</h3>
          <p>HiCapy uses AI to classify and filter spam, promotional content, and irrelevant messages. You acknowledge that this filtering is imperfect and important messages may occasionally be missed or misclassified.</p>
        </section>

        <section id="section-9">
          <h2>9. Fees and Automatic Renewal</h2>
          <h3>9.1 Subscription Fees</h3>
          <p>Fees are specified on the applicable Order Form or Pricing Page. Payment obligations are non-cancelable, and fees paid are non-refundable except as required by law.</p>
          <h3>9.2 Automatic Renewal</h3>
          <p className="caps-warning">UNLESS YOU OPT OUT OF AUTO-RENEWAL OR CANCEL YOUR SUBSCRIPTION AT LEAST THIRTY (30) DAYS BEFORE THE END OF THE CURRENT TERM, YOUR SUBSCRIPTION WILL AUTOMATICALLY RENEW FOR A PERIOD EQUAL TO THE PRIOR TERM AT OUR THEN-CURRENT PRICING.</p>
          <h3>9.3 Usage-Based Fees</h3>
          <p>Certain features may incur additional usage-based fees (e.g., API calls exceeding plan limits, storage beyond allocated capacity). You will be notified of such fees before they are incurred where reasonably practicable.</p>
          <h3>9.4 Free Tier Limitations</h3>
          <p>Free tier users may experience limitations on features, usage caps, response times, and data retention periods. HiCapy reserves the right to modify free tier limitations at any time.</p>
        </section>

        <section id="section-10">
          <h2>10. Proprietary Rights</h2>
          <h3>10.1 Customer Ownership</h3>
          <p>You retain all rights, title, and interest in your Customer Data, including emails, meeting recordings, transcripts, and documents.</p>
          <h3>10.2 HiCapy Ownership</h3>
          <p>HiCapy retains all rights to:</p>
          <ul>
            <li>The Services, underlying software, and platform architecture</li>
            <li>The AI models, algorithms, and machine learning systems</li>
            <li>The Knowledge Graph structure and methodology (but not your specific data)</li>
            <li>Usage data, aggregated statistics, and performance metrics</li>
            <li>Any improvements or derivative works created by HiCapy</li>
          </ul>
          <h3>10.3 AI-Generated Output</h3>
          <p>While you own the Customer Data used as input, AI-generated Output is jointly owned. You have the right to use Output for your purposes, while HiCapy retains the right to use anonymized patterns from Output to improve the Service.</p>
        </section>

        <section id="section-11">
          <h2>11. Confidentiality</h2>
          <h3>11.1 Confidential Information</h3>
          <p>Each party agrees to protect the other's Confidential Information with the same degree of care it uses to protect its own, but in no event less than reasonable care.</p>
          <h3>11.2 Customer Confidential Information</h3>
          <p>HiCapy treats as confidential: Customer Data, your communications accessed through Connected Services, your Knowledge Graph content, your business processes and workflows, and any information marked as confidential.</p>
          <h3>11.3 HiCapy Confidential Information</h3>
          <p>You agree to treat as confidential: technical specifications of the Service, AI model architectures, pricing information not publicly available, and any information marked as confidential.</p>
          <h3>11.4 Exceptions</h3>
          <p>Confidentiality obligations do not apply to information that: (a) is publicly available through no fault of the receiving party, (b) was rightfully known prior to disclosure, (c) is independently developed, or (d) must be disclosed by law.</p>
        </section>

        <section id="section-12">
          <h2>12. Warranties and Disclaimers</h2>
          <h3>12.1 "As Is" Service</h3>
          <p className="caps-warning">EXCEPT AS EXPRESSLY PROVIDED HEREIN, HICAPY IS PROVIDED "AS IS" AND "AS AVAILABLE." WE DISCLAIM ALL WARRANTIES, WHETHER EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</p>
          <h3>12.2 No Guarantee of Accuracy</h3>
          <p>HiCapy does not warrant that: (a) AI-generated Output will be accurate or error-free, (b) the Service will meet your specific requirements, (c) emails will be correctly classified, (d) scheduling suggestions will be optimal, or (e) the Service will operate without interruption.</p>
          <h3>12.3 Beta and Experimental Features</h3>
          <p>From time to time, we may offer Beta Services (marked as "Preview," "Beta," or "Experimental"). These are provided at no additional charge, with no warranty, and HiCapy shall have no liability for any harm arising from your use of Beta Services.</p>
          <h3>12.4 Third-Party Services</h3>
          <p>HiCapy is not responsible for the availability, security, or functionality of Connected Services (Gmail, Slack, Notion, etc.). Integration issues caused by third-party service changes are not HiCapy's responsibility.</p>
        </section>

        <section id="section-13">
          <h2>13. Limitation of Liability</h2>
          <p className="caps-warning">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
          <h3>13.1 Exclusion of Damages</h3>
          <p className="caps-warning">IN NO EVENT SHALL HICAPY BE LIABLE FOR ANY INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES, INCLUDING BUT NOT LIMITED TO:</p>
          <ul>
            <li>Loss of business, revenue, or profits</li>
            <li>Loss of data or cost of replacement services</li>
            <li>Damage to reputation or goodwill</li>
            <li>Errors in AI-generated emails or scheduling</li>
            <li>Unauthorized actions due to system errors</li>
            <li>Misclassification of important emails as spam</li>
            <li>Business interruptions or delays</li>
          </ul>
          <h3>13.2 Liability Cap</h3>
          <p className="caps-warning">HICAPY'S TOTAL AGGREGATE LIABILITY ARISING OUT OF THIS AGREEMENT SHALL NOT EXCEED THE AMOUNT PAID BY YOU DURING THE TWELVE (12) MONTHS PRIOR TO THE EVENT GIVING RISE TO THE CLAIM. FOR FREE USERS, LIABILITY IS CAPPED AT $100 USD.</p>
          <h3>13.3 Actions Taken by AI</h3>
          <p>You acknowledge that you are responsible for reviewing and approving actions before they are executed. HiCapy is not liable for consequences of actions you approved, even if the AI's suggestion was suboptimal.</p>
        </section>

        <section id="section-14">
          <h2>14. Term and Termination</h2>
          <h3>14.1 Term</h3>
          <p>This Agreement begins when you first access the Services and continues until terminated by either party.</p>
          <h3>14.2 Termination for Breach</h3>
          <p>Either party may terminate this Agreement if the other party materially breaches the agreement and fails to cure such breach within 30 days of written notice.</p>
          <h3>14.3 Termination for Convenience</h3>
          <p>You may terminate this Agreement at any time through your account settings. Termination will be effective at the end of your current billing period. No refunds will be provided for partial periods.</p>
          <h3>14.4 Effect of Termination</h3>
          <p>Upon termination:</p>
          <ul>
            <li>Your right to use the Service ceases immediately</li>
            <li>HiCapy will cease all Autonomous Actions on your behalf</li>
            <li>Access to Connected Services will be revoked</li>
            <li>Your Customer Data will be available for download for 30 days</li>
            <li>After 30 days, HiCapy may delete your Customer Data and Knowledge Graph</li>
          </ul>
          <h3>14.5 Survival</h3>
          <p>Sections relating to confidentiality, proprietary rights, limitations of liability, and dispute resolution will survive termination.</p>
        </section>

        <section id="section-15">
          <h2>15. Dispute Resolution and Governing Law</h2>
          <h3>15.1 Governing Law</h3>
          <p>This Agreement shall be governed by the laws of India, without regard to its conflict of laws principles.</p>
          <h3>15.2 Arbitration</h3>
          <p>Any dispute arising out of this Agreement shall be settled by binding arbitration in accordance with the Arbitration and Conciliation Act, 1996. The seat of arbitration shall be Bangalore, India.</p>
          <h3>15.3 Class Action Waiver</h3>
          <p className="caps-warning">ALL CLAIMS MUST BE BROUGHT IN THE PARTIES' INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING. YOU WAIVE ANY RIGHT TO A JURY TRIAL.</p>
          <h3>15.4 Equitable Relief</h3>
          <p>Notwithstanding the arbitration requirement, either party may seek injunctive or equitable relief in a court of competent jurisdiction to protect intellectual property rights or confidential information.</p>
        </section>

        <section id="section-16">
          <h2>16. Future Features and Service Changes</h2>
          <h3>16.1 Planned Features</h3>
          <p>HiCapy may introduce additional features in the future, including but not limited to:</p>
          <ul>
            <li>WhatsApp integration for mobile approvals</li>
            <li>Advanced meeting scheduling with automatic slot optimization</li>
            <li>Integration with additional communication platforms</li>
            <li>Enhanced workflow automation capabilities</li>
            <li>Deeper integration with enterprise tools</li>
            <li>Voice-based interaction modes</li>
          </ul>
          <h3>16.2 Beta Access</h3>
          <p>You may be offered early access to new features. By accepting beta access, you agree to provide feedback and acknowledge the experimental nature of such features.</p>
          <h3>16.3 Service Modifications</h3>
          <p>HiCapy reserves the right to modify, suspend, or discontinue any aspect of the Service at any time. We will provide reasonable notice of material changes that negatively impact functionality.</p>
          <h3>16.4 API and Integration Changes</h3>
          <p>Third-party services (Gmail, Slack, etc.) may change their APIs or terms of service. HiCapy will make commercially reasonable efforts to maintain integrations but cannot guarantee continued compatibility.</p>
        </section>

        <section id="section-17">
          <h2>17. Miscellaneous</h2>
          <h3>17.1 Entire Agreement</h3>
          <p>This Agreement constitutes the entire agreement between you and HiCapy regarding the Services and supersedes all prior agreements and understandings.</p>
          <h3>17.2 Amendments</h3>
          <p>HiCapy may update these Terms at any time. Material changes will be notified via email or through the Service. Continued use after changes constitutes acceptance.</p>
          <h3>17.3 Assignment</h3>
          <p>You may not assign this Agreement without HiCapy's prior written consent. HiCapy may assign this Agreement in connection with a merger, acquisition, or sale of assets.</p>
          <h3>17.4 Severability</h3>
          <p>If any provision of this Agreement is held invalid or unenforceable, the remaining provisions will continue in full force and effect.</p>
          <h3>17.5 Waiver</h3>
          <p>Failure to enforce any provision of this Agreement does not constitute a waiver of that provision or any other provision.</p>
          <h3>17.6 Contact Information</h3>
          <div className="contact-box" style={{marginTop: '1.5rem', padding: '1.5rem', background: '#f9fafb', borderRadius: '8px', borderLeft: '4px solid #4f46e5'}}>
            <p style={{margin: '0 0 1rem 0'}}>For legal inquiries, support requests, or concerns regarding these terms, please contact:</p>
            <p style={{margin: '0'}}><strong>Email:</strong> <a href="mailto:legal.hicapy@gmail.com" style={{color: '#4f46e5', textDecoration: 'none'}}>legal.hicapy@gmail.com</a></p>
            <p style={{margin: '0.5rem 0 0 0'}}><strong>Address:</strong> HiCapy, Bangalore, India</p>
          </div>
        </section>
      </main>

      <aside className="sidebar">
        <div className="sticky-nav">
          <h3>On this page</h3>
          <ul className="nav-links">
            {navItems.map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className={activeId === item.id ? 'active' : ''}
                  onClick={(e) => scrollToSection(e, item.id)}
                >
                  {item.label}
                </a>
              </li>
            ))}
            <li key="section-17">
              <a
                href="#section-17"
                className={activeId === 'section-17' ? 'active' : ''}
                onClick={(e) => scrollToSection(e, 'section-17')}
              >
                17. Miscellaneous
              </a>
            </li>
          </ul>
        </div>
      </aside>
    </div>
  );
};

export default TermsOfService;